import { BaseService } from '../BaseService';
import AWS, { S3 } from 'aws-sdk';
import { ValidationError, StorageError } from '../../utils/errors';
import { createHash } from 'crypto';
import { StateDocumentRequirement } from '../StateFilingAutomationService';
import { 
  DocumentType, 
  RetentionPolicy, 
  SensitivityLevel, 
  DocumentMetadata as BaseDocumentMetadata,
  DocumentUploadResponse,
  DocumentData,
  DocumentSearchParams,
  DocumentStatus
} from '../../types/documents';
import { createGzip, gzip } from 'zlib';
import { promisify } from 'util';
import { createCipheriv, randomBytes } from 'crypto';
import { QueryResult } from 'pg';
import { apiInstance } from '../api';

export interface ValidationResult {
  status: string;
  errors?: string[];
  warnings?: string[];
}

export { DocumentType, RetentionPolicy, SensitivityLevel };

interface S3UploadError extends Error {
  message: string;
}

interface DocumentRow {
  id: string;
  name: string;
  created_at: Date;
  status: string;
  storage_key: string;
  metadata: BaseDocumentMetadata;
}

interface FormTemplate {
  type: string;
  path: string;
  fields: string[];
  required: boolean;
}

interface FormGenerationResult {
  type: string;
  content: Buffer;
  filename: string;
  metadata: Record<string, any>;
}

interface GeneratedDocument {
  type: string;
  content: Buffer;
  filename: string;
  metadata: Record<string, any>;
}

interface FormData {
  [key: string]: any;
}

export class DocumentService extends BaseService {
  private s3: S3;
  private readonly bucketName: string;
  private readonly ENCRYPTION_KEY: Buffer;
  private readonly ENCRYPTION_ALGORITHM = 'aes-256-cbc';
  private gzipPromise = promisify(gzip);
  private baseUrl = '/api/documents';

  constructor() {
    super();
    this.s3 = new AWS.S3({
      region: process.env.AWS_REGION,
      accessKeyId: process.env.AWS_ACCESS_KEY_ID,
      secretAccessKey: process.env.AWS_SECRET_ACCESS_KEY
    });
    this.bucketName = process.env.AWS_S3_BUCKET || '';
    this.ENCRYPTION_KEY = Buffer.from(process.env.ENCRYPTION_KEY || '', 'hex');
  }

  private async compressFile(file: Buffer): Promise<Buffer> {
    try {
      return await this.gzipPromise(file) as Buffer;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during compression';
      throw new Error(`Failed to compress file: ${errorMessage}`);
    }
  }

  private async encryptFile(file: Buffer): Promise<Buffer> {
    const iv = randomBytes(16);
    const cipher = createCipheriv(this.ENCRYPTION_ALGORITHM, this.ENCRYPTION_KEY, iv);
    
    const encrypted = Buffer.concat([
      iv,
      cipher.update(file),
      cipher.final()
    ]);

    return encrypted;
  }

  async storeDocument(
    file: Buffer,
    metadata: BaseDocumentMetadata,
    options: {
      encryption?: boolean;
      compression?: boolean;
    } = {}
  ): Promise<string> {
    return this.withTransaction(async (client) => {
      // Generate document hash
      const hash = this.generateDocumentHash(file);
      
      // Create document record
      const documentId = await this.createDocumentRecord(client, {
        hash,
        metadata,
        size: file.length,
        ...options
      });

      // Create audit trail
      await this.createDocumentAudit(client, {
        documentId,
        action: 'CREATED',
        metadata
      });

      return documentId;
    });
  }

  private async createDocumentRecord(client: any, data: {
    hash: string;
    metadata: BaseDocumentMetadata;
    size: number;
    encryption?: boolean;
    compression?: boolean;
  }): Promise<string> {
    const { rows } = await client.query(
      `INSERT INTO documents (
        hash, metadata, size, encryption, compression
      ) VALUES ($1, $2, $3, $4, $5)
      RETURNING id`,
      [data.hash, data.metadata, data.size, data.encryption, data.compression]
    );
    return rows[0].id;
  }

  private async createDocumentAudit(client: any, data: {
    documentId: string;
    action: string;
    metadata: BaseDocumentMetadata;
  }): Promise<void> {
    await client.query(
      `INSERT INTO document_audit (
        document_id, action, metadata
      ) VALUES ($1, $2, $3)`,
      [data.documentId, data.action, data.metadata]
    );
  }

  private generateDocumentHash(file: Buffer): string {
    const crypto = require('crypto');
    return crypto.createHash('sha256').update(file).digest('hex');
  }

  private async processFile(
    file: Buffer,
    options: {
      encryption?: boolean;
      compression?: boolean;
    }
  ): Promise<Buffer> {
    let processedFile = file;

    if (options.compression) {
      processedFile = await this.compressFile(processedFile);
    }

    if (options.encryption) {
      processedFile = await this.encryptFile(processedFile);
    }

    return processedFile;
  }

  private generateStorageKey(
    metadata: BaseDocumentMetadata,
    hash: string
  ): string {
    return `${metadata.fiscalSponsorId}/${metadata.type}/${hash}`;
  }

  private async uploadToS3(
    file: Buffer,
    key: string,
    metadata: BaseDocumentMetadata
  ): Promise<void> {
    try {
      const s3Metadata: { [key: string]: string } = {
        fiscalSponsorId: metadata.fiscalSponsorId,
        documentType: metadata.type,
        sensitivity: metadata.sensitivity,
        category: metadata.category,
        retention: metadata.retention,
        tagsJson: JSON.stringify(metadata.tags || [])
      };

      if (metadata.requirementId) {
        s3Metadata.requirementId = metadata.requirementId;
      }
      if (metadata.status) {
        s3Metadata.status = metadata.status;
      }
      if (metadata.projectId) {
        s3Metadata.projectId = metadata.projectId;
      }

      await this.s3.putObject({
        Bucket: this.bucketName,
        Key: key,
        Body: file,
        Metadata: s3Metadata
      }).promise();
    } catch (error: unknown) {
      console.error('Error uploading to S3:', error);
      throw new Error('Failed to upload document to S3');
    }
  }

  async getDocuments(): Promise<DocumentData[]> {
    return this.withTransaction(async (client) => {
      const result: QueryResult<DocumentRow> = await client.query(
        'SELECT * FROM documents WHERE deleted_at IS NULL'
      );
      
      return result.rows.map((doc: DocumentRow) => ({
        id: doc.id,
        name: doc.name,
        uploadDate: new Date(doc.created_at),
        status: doc.status as DocumentStatus,
        downloadUrl: this.generateDownloadUrl(doc.storage_key),
        metadata: doc.metadata,
        url: this.generateDownloadUrl(doc.storage_key)
      }));
    });
  }

  async uploadDocument(file: File, metadata: BaseDocumentMetadata): Promise<DocumentData> {
    return this.withTransaction(async (client) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify({
        ...metadata,
        tags: metadata.tags || [],
        sensitivity: metadata.sensitivity || SensitivityLevel.CONFIDENTIAL,
        retention: metadata.retention || RetentionPolicy.SEVEN_YEARS,
        category: metadata.category || 'GENERAL'
      }));

      const response = await apiInstance.post<DocumentUploadResponse>(`${this.baseUrl}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return {
        id: response.data.id,
        name: metadata.name,
        uploadDate: new Date(),
        status: 'PENDING' as DocumentStatus,
        downloadUrl: response.data.url,
        metadata: response.data.metadata,
        url: response.data.url
      };
    });
  }

  async getDocument(id: string): Promise<DocumentUploadResponse> {
    const response = await apiInstance.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async searchDocuments(params: DocumentSearchParams): Promise<DocumentData[]> {
    return this.withTransaction(async () => {
      const response = await apiInstance.get<DocumentUploadResponse[]>(`${this.baseUrl}/search`, { params });
      return response.data.map(doc => ({
        id: doc.id,
        name: doc.metadata.name || 'Untitled',
        uploadDate: new Date(doc.metadata.createdAt || Date.now()),
        status: doc.metadata.status as DocumentStatus,
        downloadUrl: doc.url,
        metadata: doc.metadata,
        url: doc.url
      }));
    });
  }

  async updateMetadata(id: string, metadata: Partial<BaseDocumentMetadata>): Promise<DocumentUploadResponse> {
    const response = await apiInstance.patch(`${this.baseUrl}/${id}/metadata`, metadata);
    return response.data;
  }

  async deleteDocument(id: string): Promise<void> {
    await apiInstance.delete(`${this.baseUrl}/${id}`);
  }

  private generateDownloadUrl(storageKey: string): string {
    return `${this.baseUrl}/download/${storageKey}`;
  }

  private async getStateFormTemplates(state: string): Promise<FormTemplate[]> {
    // Implementation to fetch templates from storage
    return [];
  }

  private async generateForm(
    template: FormTemplate, 
    formData: FormData
  ): Promise<FormGenerationResult> {
    // Implementation of form generation
    throw new Error('Not implemented');
  }

  async generateForms(
    templates: FormTemplate[], 
    formData: FormData, 
    state: string
  ): Promise<GeneratedDocument[]> {
    const documents: GeneratedDocument[] = [];

    try {
      // If no templates provided, fetch state-specific templates
      const formTemplates = (templates.length > 0 
        ? templates 
        : await this.getStateFormTemplates(state)) as FormTemplate[];
      
      // Generate each required form
      for (const template of formTemplates) {
        const document = await this.generateForm(template, formData);
        documents.push({
          type: template.type,
          content: document.content,
          filename: document.filename,
          metadata: {
            state,
            formType: template.type,
            generatedAt: new Date(),
            ...document.metadata
          }
        });
      }

      return documents;
    } catch (error) {
      console.error('Failed to generate forms:', error);
      throw new Error(`Failed to generate forms for state ${state}`);
    }
  }

  async generatePortalForms(state: string, formData: any) {
    const documents = await this.generateForms([], formData, state);
    return documents.map(doc => ({
      ...doc,
      portalFormatted: true
    }));
  }

  async prepareEmailSubmission(state: string, documents: any) {
    return {
      emailBody: this.generateEmailBody(state),
      attachments: documents,
      instructions: this.getEmailInstructions(state)
    };
  }

  private generateEmailBody(state: string): string {
    // Implementation
    return '';
  }

  private getEmailInstructions(state: string): any {
    // Implementation
    return {};
  }

  async validateDocument(docId: string): Promise<ValidationResult> {
    return this.withTransaction(async () => {
      const response = await apiInstance.post<ValidationResult>(
        `${this.baseUrl}/${docId}/validate`
      );
      return response.data;
    });
  }
} 